import {
	ReactElement,
	ReadonlyRefObject,
	RefObject,
	useCallback,
	useLayoutEffect,
	useRef,
	useState
} from "react";
import { LiveTileContent, TileScrollableHandle } from "../../LiveTileContent";
import { ListItem } from "../../ListItem";
import styles from "./EventList.module.less";
import {
	generateTrackingId,
	isNil,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { BusyIndicator } from "client/Components";
import {
	Appointment,
	DateToDisplay
} from "../CalendarLiveTileContent";
import { OnlineMeetingButton } from "client/Features/Calendar/OnlineMeetingButton";
import { formatDate } from "client_legacy/ndaw/dateFormatting";
import { localize } from "client_legacy/ndaw/culture";
import { EventListDateOffset } from "../CalendarView/CalendarView";
import {
	useResizeObserver,
	useSynchronizedRef
} from "client/Hooks";
import { sortBy } from "client_legacy/ndaw/arrayHelpers";
import { MeetingProductKind } from "client/Api/Notifications/notifications";
import { TimeZone } from "client/DateAndTime/timezones";
import {
	areOnTheSameDay,
	parseDtoDate
} from "client/DateAndTime/dateTimeUtils";
import { useShouldShowMeetingButton } from "client/Features/Calendar/hooks";
import { CalendarDestinationApp, DestinationWindow } from "client/Api/Apps/common";
import { useCalendarClient } from "client/Features/Calendar/useCalendarClient";
import { formatCalendarLocations } from "client/Features/Calendar/calendarUtils";

export type EventListProps = {
	datesToDisplay: readonly DateToDisplay[];
	errorMessage: string | nil;
	trackingId: string;
	hasShortJoinButton?: boolean;
	dateToScrollTo?: Date | null;
	isUpdating?: boolean;
	shouldShowLoadingIndicator?: boolean;
	onUnsetDateToScrollTo?(): void;
	onDateChange?: (newValue: Date) => void;
	timezone: TimeZone;
	destinationApp: CalendarDestinationApp;
	destinationWindow: DestinationWindow;
	onListItemClick(): void;
	calendarId: string;
};

export const EventList = (props: EventListProps, ): ReactElement => {
	const {
		dateToScrollTo,
		datesToDisplay,
		errorMessage,
		trackingId,
		hasShortJoinButton = false,
		isUpdating = false,
		shouldShowLoadingIndicator = false,
		onUnsetDateToScrollTo,
		onDateChange,
		timezone,
		destinationApp,
		destinationWindow,
		onListItemClick,
		calendarId
	} = props;

	const [eventListDatesOffsets, setEventListDatesOffsets] = useState<EventListDateOffset[]>([]);

	const daysRefs: ReadonlyRefObject<Map<string, DayRefValue>> = useRef<Map<string, DayRefValue>>(
		new Map()
	);

	const onUnsetDateToScrollToRef: ReadonlyRefObject<typeof onUnsetDateToScrollTo> = useSynchronizedRef(onUnsetDateToScrollTo);
	const daysContainerRef: RefObject<HTMLUListElement | null> = useRef<HTMLUListElement>(null);
	const scrollableRef: RefObject<TileScrollableHandle | null> = useRef<TileScrollableHandle>(null);
	const scrollableContainerRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const [calendarClientElement, calendarClient] = useCalendarClient({
		appConfiguration: {
			destinationApp: destinationApp,
			destinationWindow: destinationWindow
		}
	});

	const openAppointmentHandler = useCallback(
		(appointmentId: string): void => {
			calendarClient.openDetails({
				 appointmentIdentifier: {
					id: appointmentId,
					calendarId
				}
			});
		},
		[calendarClient, calendarId]
	);

	const {
		height: daysContainerHeight,
		wasInitialized
	} = useResizeObserver(daysContainerRef);

	const {
		height: scrollableContainerHeight
	} = useResizeObserver(scrollableContainerRef);

	useLayoutEffect(
		() => {
			if (isUpdating) {
				return;
			}

			hasImplicitDependencyOn(daysContainerHeight);

			const datesOffsets: EventListDateOffset[] = [];

			sortBy(
				[
					...daysRefs.current
						.values()
				],
				x => x.index
			).forEach(dayRef => {
				const id: string | null = dayRef.element
					.getAttribute("data-id");

				const date: Date | undefined = datesToDisplay.find(x => x.uniqueKey === id)
					?.date.value;
				if (isNotNil(date)) {
					datesOffsets.push({
						date,
						offset: dayRef.element.offsetTop
					});
				}
			});

			setEventListDatesOffsets(datesOffsets);
		},
		[
			datesToDisplay,
			daysContainerHeight,
			isUpdating,
			wasInitialized
		]
	);

	useLayoutEffect(
		(): void => {
			if (!dateToScrollTo || !wasInitialized || isUpdating) {
				return;
			}

			const offset: number | undefined = eventListDatesOffsets.find(
					x => areOnTheSameDay(x.date, dateToScrollTo, timezone)
				)?.offset;

			if (isNotNil(offset)) {
				scrollableRef.current
					?.scrollTo(offset);
			}
		},
		[
			dateToScrollTo,
			eventListDatesOffsets,
			timezone,
			wasInitialized,
			isUpdating
		]
	);

	const setDaysRefs = useCallback(
		(
			id: string,
			index: number,
			element: HTMLLIElement | null
		): void => {
			if (isNil(element)) {
				daysRefs.current
					.delete(id);

				return;
			}

			daysRefs.current
				.set(
					id,
					{
						index,
						element
					}
				);
		},
		[]
	);

	const scrollHandler = useCallback(
		(scrollTop: number): void => {
			if (!wasInitialized) {
				return;
			}

			const newValue: Date | undefined = [...eventListDatesOffsets].reverse()
				.find(
					(x) => x.offset <= scrollTop
				)?.date;

			if (!newValue) {
				return;
			}

			if (dateToScrollTo && areOnTheSameDay(dateToScrollTo, newValue, timezone)) {
				onUnsetDateToScrollToRef.current?.();
				return;
			}

			if (!dateToScrollTo) {
				onDateChange?.(newValue);
			}
		},
		[
			wasInitialized,
			eventListDatesOffsets,
			dateToScrollTo,
			timezone,
			onUnsetDateToScrollToRef,
			onDateChange
		]
	);

	const clickHandler = (appointment: Appointment): void => {
		onListItemClick();

		if (isNotNil(appointment.id)) {
			openAppointmentHandler(appointment.id);
		}
	};

	const renderAppointments = (appointments: readonly Appointment[]): ReactElement[] => {
		return appointments.map(a => (
			<ListItem
				key={a.id}
				onClick={() => clickHandler(a)}
				isHoverable
				trackingId={generateTrackingId("appointment", `|${a.id}`)}
				title={a.subject}
			>
				<div className={styles.timeSpan}>
					<ListItem.Title trackingId="start-time">
						{a.startTime}
					</ListItem.Title>
					{a.finishTime && (
						<ListItem.Info trackingId="end-time">
							{a.finishTime}
						</ListItem.Info>
					)}
				</div>
				<ListItem.Content>
					<ListItem.Row>
						<ListItem.Title trackingId="title">
							{a.subject}
						</ListItem.Title>
					</ListItem.Row>
					<ListItem.Row>
						<ListItem.Info trackingId="location">
							{formatCalendarLocations(a.locations)}
						</ListItem.Info>
					</ListItem.Row>
				</ListItem.Content>
				{a.meeting && a.start
					&& a.end
					&& (
						<OnlineMeetingButtonWrapper
							start={parseDtoDate(a.start)}
							end={parseDtoDate(a.end)}
							product={a.meeting.product}
							url={a.meeting.url}
							hasOnlyIcon={hasShortJoinButton}
						/>
					)
				}
			</ListItem>
		));
	};

	const renderList = (): ReactElement => (
		<ul
			{...setTrackingId(trackingId)}
			className={styles.root}
			ref={daysContainerRef}
		>
			{datesToDisplay.map((x: DateToDisplay, index: number) => (
				<li
					{...setTrackingId("day-item", `|${x.uniqueKey}`)}
					key={x.uniqueKey}
					data-id={x.uniqueKey}
					ref={ref => setDaysRefs(x.uniqueKey, index, ref)}
					style={
						index === datesToDisplay.length - 1 && onDateChange
						? {
							minHeight: scrollableContainerHeight + 1
						}
						: undefined
					}
				>
					<LiveTileContent.ContentHeader
						isHighlighted={x.date.isToday}
						text={`${
							formatDate(x.date.value, "DayName", timezone)
						}, ${
							formatDate(x.date.value, "DM(Y)", timezone)
						}`}
					/>
					<div>
						<LiveTileContent.List trackingId="appointment-list">
							{x.appointments.length === 0
								? (
									<ListItem
										trackingId="appointment-message"
									>
										<p className={styles.errorMessage}>
											{localize("_NoEvents")}
										</p>
									</ListItem>
								) : renderAppointments(x.appointments)
							}
						</LiveTileContent.List>
					</div>
				</li>
			))}
		</ul>
	);

	return (
		<div
			className={styles.scrollableContainer}
			ref={scrollableContainerRef}
		>
			<LiveTileContent.Scrollable
				ref={scrollableRef}
				onScroll={scrollHandler}
			>
				{errorMessage
					? (
						<LiveTileContent.NoResults
							icon="gear"
							title={localize("_UnableToRetrieveItems")}
							message={errorMessage}
							trackingId="has-error"
						/>
					) : renderList()
				}
				{calendarClientElement}
			</LiveTileContent.Scrollable>
			{shouldShowLoadingIndicator && isUpdating && (
				<BusyIndicator />
			)}
		</div>
	);
};

EventList.displayName = "EventList";

type DayRefValue = {
	index: number;
	element: HTMLLIElement;
};

type OnlineMeetingButtonWrapperProps = {
	url: string;
	product: MeetingProductKind;
	start: Date;
	end: Date;
	hasOnlyIcon?: boolean;
};

const OnlineMeetingButtonWrapper = (props: OnlineMeetingButtonWrapperProps): ReactElement | null => {
	const {
		url,
		product,
		end,
		start,
		hasOnlyIcon
	} = props;

	const shouldShowButton: boolean= useShouldShowMeetingButton(
		MINUTES_TO_SHOW_MEETING_BUTTON,
		start,
		end
	);

	if (!shouldShowButton) {
		return null;
	}

	return (
		<div className={styles.onlineMeetingButtonWrapper}>
			<OnlineMeetingButton
				url={url}
				product={product}
				trackingId="join-meeting"
				hasOnlyIcon={hasOnlyIcon}
			/>
		</div>
	);
};

const MINUTES_TO_SHOW_MEETING_BUTTON: number = 15;